import type { FilterOption, ListPageConfig } from '@/lib/filters/types'
import { DEFAULT_RELATED_ORGS_LIMIT } from '@/lib/relationships/use-related-orgs'
import { formatStatusLabel } from '@/lib/status_colors'
import { CLIENT_SORT_FIELD_OPTIONS } from './client-sort'

/**
 * Static config for the SA clients list page — used by the route file's
 * `validateSearch` and as a base for the dynamic config inside the component.
 *
 * Dynamic field properties (options, onSearch, isLoading, resolveLabel, footer)
 * are added inside the component via useMemo.
 */
export const SA_CLIENTS_ROUTE_CONFIG: ListPageConfig = {
  routeId: '/clients/',
  pageId: 'sa-clients',
  fields: [
    { id: 'q', label: 'Search', type: 'search', placeholder: 'Search clients…' },
    { id: 'country', label: 'Country', type: 'multi-select' },
    { id: 'managed_by', label: 'Managed by', type: 'async-select' },
  ],
  pagination: { pageSize: DEFAULT_RELATED_ORGS_LIMIT },
  // EW-T sidebar-filter-rollout — the SA `relatedOrgs` query has no `sort` argument at all; its one
  // fixed server order is `name ASC` (see `useRelatedOrgs`'s own doc comment). Every OTHER option
  // here is therefore page-local for this branch (re-sorts the loaded page only — see ClientsPage's
  // `isSaPageLocalSort`), unlike the agency branch where `name`/`entityCount` can be server-backed.
  sort: { options: CLIENT_SORT_FIELD_OPTIONS, default: { key: 'name', order: 'asc' } },
}

/**
 * T0029's `OnboardingState` enum — the live / not-live BINARY (REJECTED/DECLINED bucket into
 * ONBOARDING server-side). Sourced from the real codegen'd enum (`src/gql/graphql.ts`), not invented.
 *
 * ⚠️ This is NOT what the row shows any more. Since EW-FIX-08 the row's chip is the five-state
 * `ClientLifecycleChip` (`RelatedOrg.lifecycleState`: Complete / Onboarding / Ready for review /
 * Rejected / Not started), so this filter offers TWO states while the chip renders FIVE. That is a
 * known inconsistency, raised in EW-FIX-71 and left for a product decision (the filter would send
 * `lifecycleStates`, and then has to compose with the Onboarding saved view, which sends the same
 * argument). Until then, do not read "the only two values the chip renders" into this list: that
 * sentence described behaviour EW-FIX-08 removed.
 */
const ACTIVATION_STATE_OPTIONS: FilterOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ONBOARDING', label: 'Onboarding' },
]

/**
 * `AgreementDisplayStatus` enum — the closed set `useAgreementStatusForParties`
 * (agency path) resolves to. `CONTRACT_MISSING`/`CONTRACT_NOT_VISIBLE` are
 * deliberately excluded: those are `RelatedOrg.agreementStatus` (SA/relatedOrgs
 * path) sentinels, not values `agreementStatusForParties` returns — this filter
 * only needs to match what the agency branch's own data source can produce.
 */
const AGREEMENT_STATE_OPTIONS: FilterOption[] = [
  'ACTIVE', 'AWAITING_SIGNATURE', 'SENT', 'EXPIRING_SOON', 'EXPIRED', 'DECLINED', 'INACTIVE',
].map((value) => ({ value, label: formatStatusLabel(value) }))

/**
 * Agency clients list — the ENT-263 cut-over target (findings #5/#8/#9).
 *
 * `service_scope` deliberately carries NO static `options` here. Unlike
 * activation/agreement state, service scope is NOT a closed GraphQL enum —
 * `RelatedOrgRelationship.scope` / `RelationshipRowVM.services` is
 * `Array<String>` (free-text tags; `scopeLabel()` humanises whatever string
 * it's given rather than looking it up in a fixed map). The task doc's
 * instruction to "confirm the exact enum values... do not invent values"
 * could not be satisfied with a static list because no such enum exists —
 * confirmed by reading `src/gql/graphql.ts`'s `RelatedOrgRelationship.scope`
 * field and `ScopeChips`/`scopeLabel`. Options are therefore supplied at
 * runtime in `ClientsPage.tsx`, derived from the currently-loaded rows'
 * `scopes`, the same pattern `country` already uses on both branches
 * (`useOrgCountryOptions` / `agencyCountryOptions`) rather than a fixed list.
 *
 * `country`'s `urlKey` is namespaced (`a_country`) so the agency and SA
 * branches' distinct option sets never fight over the same query param if a
 * user's persona flips mid-session — see T02's task doc.
 */
export const AGENCY_CLIENTS_ROUTE_CONFIG: ListPageConfig = {
  routeId: '/clients/',
  pageId: 'agency-clients',
  fields: [
    { id: 'q', label: 'Search', type: 'search', placeholder: 'Search clients…' },
    { id: 'country', label: 'Jurisdiction', type: 'multi-select', urlKey: 'a_country' },
    {
      id: 'activation_state',
      label: 'Activation state',
      type: 'multi-select',
      options: ACTIVATION_STATE_OPTIONS,
    },
    {
      id: 'agreement_state',
      label: 'Agreement state',
      type: 'multi-select',
      options: AGREEMENT_STATE_OPTIONS,
    },
    { id: 'service_scope', label: 'Service scope', type: 'multi-select' },
  ],
  // Corrected 2026-09-09: page size 50 → 20 (human decision), agency branch only. NOT a
  // change to `DEFAULT_RELATED_ORGS_LIMIT` itself — that shared constant is also read by
  // `AgenciesPage.tsx`/`use-agencies.ts`, both outside this MR's scope; changing it would
  // have silently repaged an unrelated surface. Overridden here at the agency call site,
  // matching every other agency-branch-only decision in this workflow. `SA_CLIENTS_
  // ROUTE_CONFIG` (above) is untouched and still uses the shared default.
  pagination: { pageSize: 20 },
  // T05 (finding #16) / EW-FIX-12b — name (A–Z / Z–A) and entity count (most first) are ordered by
  // the SERVER (`relatedOrgsConnection(sort: RelatedOrgSort)`, see `client-sort.ts`); only a sort the
  // server has no equivalent for (Jurisdiction, or Entities ascending) re-orders the loaded page client-side. See
  // ClientsPage.tsx's `sortedAgencyRows` and `isPageLocalSort`.
  //
  // Post-ship finding (independent spec-review arm, round 7/8) — a third option,
  // `{ key: 'workerCount', label: 'Workers' }`, used to be declared here but was never reachable:
  // `ClientsTable.tsx` renders Workers in `TwoRowTable`'s row-2 shared context area, which is
  // deliberately header-less by design (both source guides: "a shared context area across the
  // remaining columns", not a second column grid) — no `sortKey` capability exists there. Removed
  // rather than wired: giving Workers a sortKey would mean moving it into row 1 as a real 7th
  // column, a genuine scope expansion (competing for the already tightly-budgeted row-1 width —
  // see ClientsTable.test.tsx's horizontal-clipping-fix tests), not a config fix. Unreachable by
  // `TwoRowTable`'s row-2 design, not an oversight — do not re-add this from the reference's
  // column list without first giving Workers a real row-1 column.
  //
  // EW-FIX-12b — the Clients page has a SERVER sort (`relatedOrgsConnection(sort: RelatedOrgSort)`):
  // `{name, asc|desc}` and `{entityCount, desc}` go to the server (see `client-sort.ts`'s
  // `serverSortFor`/`isPageLocalSort`); `country`, `{entityCount, asc}`, `attention` and `renewal`
  // have no server equivalent and stay PAGE-LOCAL re-sorts, labelled as such. The DEFAULT is
  // `{name, asc}`: the server's own default ordering, replacing the old page-local
  // Jurisdiction-ascending default. ASSUMED, not ruled (docs/tasks/EW-FIX-12b-*.md): the owner has
  // not confirmed that change.
  // `useListPageState` never returns a null sort while this block exists, so the default here is
  // what the control opens on.
  //
  // EW-T sidebar-filter-rollout — rendered through the canonical `SortControl` (toggle variant) via
  // `FilterBar`'s own `sort`/`setSortState` props, replacing the page's old bespoke flat-list
  // `ClientSortSelect`/`ClientSortControl` (the latter was dead code — never mounted).
  sort: { options: CLIENT_SORT_FIELD_OPTIONS, default: { key: 'name', order: 'asc' } },
}

/**
 * Union of both persona configs' fields, for the route's `validateSearch`
 * only (option 2 from T02's task doc) — each branch still builds its own
 * narrower runtime config for `FilterBar`/`useListPageState`. Keeps
 * `SA_CLIENTS_ROUTE_CONFIG`/`AGENCY_CLIENTS_ROUTE_CONFIG` each legible on
 * their own; the two never render simultaneously so there's no real
 * collision risk, but distinct urlKeys (`country` vs `a_country`) keep it
 * that way defensively.
 */
export const CLIENTS_ROUTE_VALIDATE_SEARCH_CONFIG: ListPageConfig = {
  routeId: '/clients/',
  pageId: 'clients-route',
  fields: [...SA_CLIENTS_ROUTE_CONFIG.fields, ...AGENCY_CLIENTS_ROUTE_CONFIG.fields],
}
